import { Button, Divider, Flex, Input, Modal, QRCode, Spin, Typography } from 'antd'
import React, { useEffect, useRef, useState } from 'react'

import { authClient, getAuthError } from '@connectors/AuthClient'
import { useNotifications } from '@store/notifications'

type QRModalProps = {
    isOpen: boolean
    onClose: () => void
    onSuccess: () => Promise<void>
    // Called when the TOTP secret could not be generated (e.g. wrong password)
    onError: () => void
    password: string
}

export const QRModal: React.FC<QRModalProps> = ({ isOpen, onClose, onSuccess, onError, password }) => {
    const [totpURI, setTotpURI] = useState<string>()
    const [backupCodes, setBackupCodes] = useState<string[]>([])
    const [verificationCode, setVerificationCode] = useState<string>('')
    const [isValidating, setIsValidating] = useState<boolean>(false)

    // Guards against StrictMode double-invoking the effect, which would generate two secrets
    const hasRequestedSecret = useRef(false)

    const { showNotification } = useNotifications()

    const secretKey = totpURI ? new URL(totpURI).searchParams.get('secret') : null

    useEffect(() => {
        if (hasRequestedSecret.current) return
        hasRequestedSecret.current = true

        const generateSecret = async () => {
            try {
                const data = await authClient.twoFactor.enable({ password }, { throw: true })

                if (data.method !== 'totp') {
                    throw new Error(`Unexpected two factor method: ${data.method}`)
                }

                setTotpURI(data.totpURI)
                setBackupCodes(data.backupCodes)
            } catch (err) {
                if (getAuthError(err)?.code === 'INVALID_PASSWORD') {
                    showNotification({
                        title: 'Error enabling MFA',
                        description: 'Please check your password and try again',
                        type: 'error'
                    })
                } else {
                    showNotification({
                        title: 'Error enabling MFA',
                        description: 'Please try again',
                        type: 'error'
                    })
                }
                onError()
            }
        }

        generateSecret()
    }, [password])

    const onValidate = async () => {
        try {
            setIsValidating(true)

            await authClient.twoFactor.verifyTotp({ code: verificationCode }, { throw: true })
            await onSuccess()
        } catch (err) {
            switch (getAuthError(err)?.code) {
                case 'INVALID_CODE':
                case 'OTP_HAS_EXPIRED':
                    showNotification({
                        title: 'Invalid or expired OTP',
                        description: 'Please try again',
                        type: 'error'
                    })
                    break
                default:
                    showNotification({
                        title: 'An error occurred while verifying the code',
                        description: 'Please try again',
                        type: 'error'
                    })
            }
        } finally {
            setIsValidating(false)
        }
    }

    return (
        <Modal open={isOpen} onCancel={onClose} footer={[]} destroyOnHidden>
            {!totpURI ? (
                <Flex justify="center" style={{ padding: 48 }}>
                    <Spin />
                </Flex>
            ) : (
                <Flex gap="middle" vertical align="center">
                    <Flex gap="xsmall" vertical align="center">
                        <Typography.Title level={5}>Scan the QR code with your authenticator app</Typography.Title>
                        <Typography.Text>Or enter the secret key manually.</Typography.Text>
                    </Flex>
                    <Divider />
                    <Flex gap="small" vertical align="center">
                        <QRCode value={totpURI} />
                        {secretKey && <Typography.Text copyable>{secretKey}</Typography.Text>}
                    </Flex>
                    <Divider />
                    <Flex gap="xsmall" vertical align="center">
                        <Typography.Text strong>Backup codes</Typography.Text>
                        <Typography.Text type="secondary">
                            Store these somewhere safe. Each can be used once if you lose your device.
                        </Typography.Text>
                        <Typography.Paragraph copyable={{ text: backupCodes.join('\n') }} code>
                            {backupCodes.join('  ')}
                        </Typography.Paragraph>
                    </Flex>
                    <Divider />
                    <Flex gap="middle">
                        <Input
                            placeholder="Enter verification code"
                            value={verificationCode}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            onChange={e => setVerificationCode(e.target.value.trim())}
                            onPressEnter={() => verificationCode && onValidate()}
                        />
                        <Button
                            onClick={onValidate}
                            loading={isValidating}
                            disabled={isValidating || !verificationCode}
                            type="primary"
                            style={{ width: '80px', alignSelf: 'flex-end' }}
                        >
                            Validate
                        </Button>
                    </Flex>
                </Flex>
            )}
        </Modal>
    )
}
